import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { withPermission } from '../../withPermission';
import { HealthStrip } from './HealthStrip';
import { QueueGroups, type QueueGroupRow } from './QueueGroups';
import { DangerZone } from './DangerZone';
import { NeedsAttention } from './NeedsAttention';
import {
  bridgeVerdict,
  instantaneousDiagnoses,
  sustainedDiagnoses,
  type DiagnosisStreaks,
  type QueueDiagnosisKind,
} from './diagnoses';
import { testIds } from './testIds';

// The stat counts and the row list poll on the same cadence so they never
// disagree: at different rates, a fast-draining queue makes the counts tick
// down while the (staler) list still reads "nothing queued", or vice versa.
// The list's course-walk join is the heavier query, but it's bounded and this
// is a briefly-viewed dev-tools page. Both pause in background tabs (react-query
// default) and refetch immediately after any mutation.
const POLL_MS = 5_000;

// At a 5s poll "Updated 0s ago" is perpetual noise; the age only appears once
// the data is genuinely stale.
const STALE_AFTER_SECONDS = 15;

// Sustained across several consecutive health polls (see diagnoses.ts); tracked
// with a ref so a render never re-runs the hysteresis.
function useQueueDiagnoses(
  health: Parameters<typeof instantaneousDiagnoses>[0] | undefined,
): Set<QueueDiagnosisKind> {
  const streaksRef = useRef<DiagnosisStreaks>(new Map());
  const lastHealthRef = useRef<typeof health>(undefined);
  const sustainedRef = useRef<Set<QueueDiagnosisKind>>(new Set());

  if (health !== undefined && health !== lastHealthRef.current) {
    const current = instantaneousDiagnoses(health);
    const result = sustainedDiagnoses(streaksRef.current, current);
    sustainedRef.current = result.sustained;
    streaksRef.current = result.streaks;
    lastHealthRef.current = health;
  }
  return sustainedRef.current;
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <Center
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      py={8}
      color="fg.muted"
      fontSize="sm"
    >
      {children}
    </Center>
  );
}

export const TranslationQueuePage = withPermission('canAccessDeveloperTools', () => {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  // The button spinner tracks only deliberate refreshes; the 5s background
  // poll must not flash it.
  const [isManuallyRefreshing, setIsManuallyRefreshing] = useState(false);

  const health = trpcAdmin.contentTranslations.queueHealth.useQuery(undefined, {
    refetchInterval: POLL_MS,
  });
  const pending = trpcAdmin.contentTranslations.listQueue.useQuery(
    { view: 'pending' },
    { refetchInterval: POLL_MS },
  );
  const failed = trpcAdmin.contentTranslations.listQueue.useQuery(
    { view: 'failed' },
    { refetchInterval: POLL_MS },
  );

  const refetchAll = async () => {
    await Promise.all([
      utils.contentTranslations.queueHealth.invalidate(),
      utils.contentTranslations.listQueue.invalidate(),
    ]);
  };

  const cancelCells = trpcAdmin.contentTranslations.cancelQueueCells.useMutation();
  const cancelCourse = trpcAdmin.contentTranslations.cancelCourseQueue.useMutation();
  const cancelAllQueued = trpcAdmin.contentTranslations.cancelAllQueued.useMutation();
  const reEnqueue = trpcAdmin.contentTranslations.reEnqueueQueued.useMutation();
  const retryStalled = trpcAdmin.contentTranslations.retryStalledQueue.useMutation();
  const retryAllFailed = trpcAdmin.contentTranslations.retryAllFailedQueue.useMutation();
  const retryOne = trpcAdmin.contentTranslations.retry.useMutation();
  const flush = trpcAdmin.contentTranslations.flushQueue.useMutation();

  const busy =
    cancelCells.isPending ||
    cancelCourse.isPending ||
    cancelAllQueued.isPending ||
    reEnqueue.isPending ||
    retryStalled.isPending ||
    retryAllFailed.isPending ||
    flush.isPending;

  const diagnoses = useQueueDiagnoses(health.data);
  const verdict = useMemo(
    () => (health.data === undefined ? { kind: 'unknown' as const } : bridgeVerdict(health.data)),
    [health.data],
  );
  const mismatch = diagnoses.has('stranded') || diagnoses.has('ghosts');

  const failToast = (title: string) => toast({ title, status: 'error', isClosable: true });

  const handleCancelCells = async (cells: QueueGroupRow[], scopeLabel: string) => {
    try {
      const result = await cancelCells.mutateAsync({
        cells: cells.map(({ sourceKind, sourceId, language }) => ({
          sourceKind,
          sourceId,
          language,
        })),
        scopeLabel,
      });
      toast({
        title:
          result.cancelled === result.requested
            ? t`Cancelled ${result.cancelled} of ${result.requested}.`
            : t`Cancelled ${result.cancelled} of ${result.requested}. The rest had already finished.`,
        status: 'success',
        isClosable: true,
      });
    } catch {
      failToast(t`Couldn't cancel. Nothing was changed.`);
    }
    await refetchAll();
  };

  const handleCancelCourse = async (courseId: string) => {
    try {
      const result = await cancelCourse.mutateAsync({ courseId });
      toast({
        title: t`Cancelled ${result.cancelled} translations.`,
        status: 'success',
        isClosable: true,
      });
    } catch {
      failToast(t`Couldn't cancel this course's translations.`);
    }
    await refetchAll();
  };

  const handleRetryRow = async (row: QueueGroupRow) => {
    const resourceVersionId = Number(row.sourceId);
    if (!Number.isInteger(resourceVersionId) || row.sourceKind === 'VIDEO_SUBTITLE') {
      return;
    }
    try {
      await retryOne.mutateAsync({
        resourceVersionId,
        resourceKind: row.sourceKind === 'CONVERSATION' ? 'CONVERSATION' : 'PAGE',
        language: row.language,
      });
      toast({ title: t`Retry queued.`, status: 'success', isClosable: true });
    } catch {
      failToast(t`Couldn't retry this translation.`);
    }
    await refetchAll();
  };

  const handleFlush = async (includeGenerating: boolean) => {
    try {
      const result = await flush.mutateAsync({ includeGenerating });
      if (result.purge.ok) {
        toast({
          title: t`Queue flushed. ${result.cancelled} rows cancelled.`,
          status: 'success',
          isClosable: true,
        });
      } else {
        toast({
          title: t`Rows were cancelled but the SQS purge failed.`,
          description: result.purge.rateLimited
            ? t`SQS allows one purge per minute. Stale messages will be skipped by the worker as they are consumed.`
            : t`Stale messages will be skipped by the worker as they are consumed.`,
          status: 'warning',
          isClosable: true,
        });
      }
    } catch {
      failToast(t`The flush failed. Refresh to see current state.`);
    }
    await refetchAll();
  };

  const bannerAction = async (run: () => Promise<{ title: string }>) => {
    try {
      const { title } = await run();
      toast({ title, status: 'success', isClosable: true });
    } catch {
      failToast(t`That didn't work. Refresh to see current state.`);
    }
    await refetchAll();
  };

  const healthData = health.data;
  const failedCount = failed.data?.totalRowCount ?? 0;
  const updatedSecondsAgo =
    health.dataUpdatedAt === 0
      ? null
      : Math.max(0, Math.round((Date.now() - health.dataUpdatedAt) / 1000));
  const showStaleness = updatedSecondsAgo !== null && updatedSecondsAgo > STALE_AFTER_SECONDS;
  const shownRowCount =
    pending.data?.courses.reduce((sum, group) => sum + group.rows.length, 0) ?? 0;

  return (
    <Box h="full" w="full" bg="bg.surface" overflowY="auto" p={4}>
      <Stack gap={8} align="stretch">
        <Stack gap={1}>
          <HStack justify="space-between" wrap="wrap">
            <PageHeading data-testid={testIds.heading}>
              <Trans>Translation Queue</Trans>
            </PageHeading>
            <HStack spacing={2}>
              {showStaleness ? (
                <Text fontSize="xs" color="fg.muted">
                  <Trans>Updated {updatedSecondsAgo}s ago</Trans>
                </Text>
              ) : null}
              <Button
                onClick={() => {
                  setIsManuallyRefreshing(true);
                  void refetchAll().finally(() => setIsManuallyRefreshing(false));
                }}
                isLoading={isManuallyRefreshing}
                leftIcon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
                size="sm"
                variant="outline"
                colorScheme="purple"
                flexShrink={0}
              >
                <Trans>Refresh</Trans>
              </Button>
            </HStack>
          </HStack>
          <Text fontSize="sm" color="fg.muted">
            <Trans>
              System-wide view of translation generation. Rows come from the database; queue depth
              comes from SQS.
            </Trans>
          </Text>
        </Stack>

        {healthData === undefined ? (
          <Center py={12}>
            <Spinner />
          </Center>
        ) : (
          <>
            <HealthStrip
              queued={healthData.db.queued}
              generating={healthData.db.generating}
              failedInWindow={healthData.db.failedInWindow}
              stalledCount={healthData.db.stalled}
              sqs={healthData.sqs}
              verdict={verdict}
              mismatch={mismatch}
            />

            {diagnoses.size > 0 ? (
              <NeedsAttention>
                {diagnoses.has('queueUnreachable') ? (
                  <NeedsAttention.Row
                    tone="warning"
                    title={<Trans>Could not read the SQS queue.</Trans>}
                    description={
                      <Trans>
                        Showing database state only. Cancels and retries still work; re-enqueue and
                        flush are disabled until the queue responds.
                      </Trans>
                    }
                  />
                ) : null}

                {diagnoses.has('stranded') ? (
                  <NeedsAttention.Row
                    tone="warning"
                    title={<Trans>Stranded rows.</Trans>}
                    description={
                      <Trans>
                        {healthData.db.queued} queued rows have no message waiting, so they will
                        never start on their own.
                      </Trans>
                    }
                    actions={
                      <>
                        <Button
                          isLoading={reEnqueue.isPending}
                          isDisabled={busy}
                          onClick={() =>
                            void bannerAction(async () => {
                              const result = await reEnqueue.mutateAsync();
                              return { title: t`Re-enqueued ${result.sent} queued rows.` };
                            })
                          }
                        >
                          <Trans>Re-enqueue</Trans>
                        </Button>
                        <Button
                          variant="outline"
                          isLoading={cancelAllQueued.isPending}
                          isDisabled={busy}
                          onClick={() =>
                            void bannerAction(async () => {
                              const result = await cancelAllQueued.mutateAsync();
                              return { title: t`Cancelled ${result.cancelled} queued rows.` };
                            })
                          }
                        >
                          <Trans>Cancel ({healthData.db.queued})</Trans>
                        </Button>
                      </>
                    }
                  />
                ) : null}

                {diagnoses.has('stalled') ? (
                  <NeedsAttention.Row
                    tone="warning"
                    title={<Trans>Stalled runs.</Trans>}
                    description={
                      <Trans>
                        {healthData.db.stalled} runs started more than{' '}
                        {healthData.stalledAfterMinutes} minutes ago and have not finished.
                      </Trans>
                    }
                    actions={
                      <Button
                        isLoading={retryStalled.isPending}
                        isDisabled={busy}
                        onClick={() =>
                          void bannerAction(async () => {
                            const result = await retryStalled.mutateAsync();
                            return { title: t`Re-queued ${result.retried} stalled runs.` };
                          })
                        }
                      >
                        <Trans>Cancel and Retry ({healthData.db.stalled})</Trans>
                      </Button>
                    }
                  />
                ) : null}

                {diagnoses.has('ghosts') ? (
                  <NeedsAttention.Row
                    tone="info"
                    title={<Trans>More messages waiting than queued rows.</Trans>}
                    description={
                      <Trans>
                        These point at rows already done or cancelled, so the worker skips them and
                        they drain on their own.
                      </Trans>
                    }
                  />
                ) : null}

                {diagnoses.has('workingBlind') ? (
                  <NeedsAttention.Row
                    tone="info"
                    title={<Trans>The worker is holding messages but no row is translating.</Trans>}
                    description={
                      <Trans>
                        Usually resolves itself; if it persists, check the translations service
                        logs.
                      </Trans>
                    }
                  />
                ) : null}
              </NeedsAttention>
            ) : null}

            <Box>
              <HStack justify="space-between" align="flex-start" mb={2}>
                <Box>
                  <Text fontSize="lg" fontWeight="semibold">
                    <Trans>Active queue</Trans>
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {healthData.db.queued + healthData.db.generating === 0 ? (
                      <Trans>Nothing queued or translating.</Trans>
                    ) : (
                      <Trans>
                        {healthData.db.queued} queued · {healthData.db.generating} translating
                      </Trans>
                    )}
                  </Text>
                </Box>
              </HStack>
              {pending.isLoading ? (
                <Center py={8}>
                  <Spinner size="sm" />
                </Center>
              ) : (pending.data?.courses.length ?? 0) === 0 ? (
                <EmptyState>
                  <Trans>Nothing queued or translating.</Trans>
                </EmptyState>
              ) : (
                <Stack gap={2}>
                  <QueueGroups
                    groups={pending.data?.courses ?? []}
                    view="pending"
                    busy={busy}
                    onCancelCells={handleCancelCells}
                    onCancelCourse={handleCancelCourse}
                    onRetryRow={handleRetryRow}
                  />
                  {pending.data?.truncated === true ? (
                    <Text fontSize="xs" color="fg.muted">
                      <Trans>
                        Showing the oldest {shownRowCount} of {pending.data.totalRowCount} rows.
                        Counts above cover everything.
                      </Trans>
                    </Text>
                  ) : null}
                </Stack>
              )}
            </Box>

            <Box>
              <HStack justify="space-between" align="flex-start" mb={2}>
                <Box>
                  <Text fontSize="lg" fontWeight="semibold">
                    <Trans>Failed</Trans>
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {failedCount === 0 ? (
                      <Trans>None in the last 7 days.</Trans>
                    ) : (
                      <Trans>{failedCount} in the last 7 days.</Trans>
                    )}
                  </Text>
                </Box>
                {failedCount > 1 ? (
                  <Button
                    size="xs"
                    variant="outline"
                    leftIcon={<RefreshCw size={iconSize.xs} />}
                    isLoading={retryAllFailed.isPending}
                    isDisabled={busy}
                    onClick={() =>
                      void bannerAction(async () => {
                        const result = await retryAllFailed.mutateAsync();
                        return { title: t`Retry queued for ${result.retried} translations.` };
                      })
                    }
                  >
                    <Trans>Retry All ({failedCount})</Trans>
                  </Button>
                ) : null}
              </HStack>
              {failedCount === 0 ? (
                <EmptyState>
                  <Trans>No failures in the last 7 days.</Trans>
                </EmptyState>
              ) : (
                <QueueGroups
                  groups={failed.data?.courses ?? []}
                  view="failed"
                  busy={busy}
                  onCancelCells={handleCancelCells}
                  onCancelCourse={handleCancelCourse}
                  onRetryRow={handleRetryRow}
                />
              )}
            </Box>

            <DangerZone
              queued={healthData.db.queued}
              generating={healthData.db.generating}
              sqsVisible={healthData.sqs.ok ? healthData.sqs.visible : null}
              lastFlush={healthData.lastFlush}
              sqsOk={healthData.sqs.ok}
              onFlush={handleFlush}
              isFlushing={flush.isPending}
            />
          </>
        )}
      </Stack>
    </Box>
  );
});
